import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { CampaignFilter } from '@internal/backstage-plugin-bidone-backend';
import {
  useCreateFilter,
  useDeleteFilter,
  useUpdateFilter,
} from '../../hooks/useCampaigns';
import { FilterBuilder, FilterDraft } from './FilterBuilder';

type FilterEditorDialogProps = {
  open: boolean;
  // When set the dialog edits this filter; otherwise it creates a new one.
  filter?: CampaignFilter | null;
  onClose: () => void;
  // Fired with the saved filter. When the user built a snapshot, snapshotRefs
  // contains the selected component refs to freeze as membership.
  onSaved?: (filter: CampaignFilter, snapshotRefs?: string[]) => void;
  // Fired with the deleted filter's id so callers can detach it from a
  // campaign (a deleted filter would otherwise leave a dangling filterId).
  onDeleted?: (filterId: string) => void;
  // When true, shows the "Build snapshot" toggle so the user can select which
  // matched components to freeze as snapshot membership.
  withSnapshot?: boolean;
};

export const FilterEditorDialog = ({
  open,
  filter,
  onClose,
  onSaved,
  onDeleted,
  withSnapshot,
}: FilterEditorDialogProps) => {
  const [name, setName] = useState('');
  const [draft, setDraft] = useState<FilterDraft>({
    combine: 'all',
    predicates: [],
  });
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [snapshotMode, setSnapshotMode] = useState(false);
  const [snapshotCount, setSnapshotCount] = useState(0);
  const snapshotRefsRef = useRef<string[]>([]);
  const createFilter = useCreateFilter();
  const updateFilter = useUpdateFilter();
  const deleteFilter = useDeleteFilter();
  const saving = createFilter.isPending || updateFilter.isPending;
  const actionError =
    createFilter.error ?? updateFilter.error ?? deleteFilter.error;

  // Re-seed local state each time the dialog opens.
  useEffect(() => {
    if (open) {
      setName(filter?.name ?? '');
      setDraft({
        combine: filter?.combine ?? 'all',
        predicates: filter?.predicates ?? [],
      });
      setConfirmingDelete(false);
      setSnapshotMode(false);
      setSnapshotCount(0);
      snapshotRefsRef.current = [];
      createFilter.reset();
      updateFilter.reset();
      deleteFilter.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, filter]);

  const handleSave = async () => {
    const saved = filter
      ? await updateFilter.mutateAsync({
          id: filter.id,
          patch: { name: name.trim(), ...draft },
        })
      : await createFilter.mutateAsync({ name: name.trim(), ...draft });
    onSaved?.(saved, snapshotMode ? snapshotRefsRef.current : undefined);
    onClose();
  };

  const handleDelete = async () => {
    if (!filter) return;
    await deleteFilter.mutateAsync({ id: filter.id });
    onDeleted?.(filter.id);
    setConfirmingDelete(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{filter ? 'Edit filter' : 'New filter'}</DialogTitle>
      <DialogContent>
        {actionError ? (
          <Alert severity="error">{(actionError as Error).message}</Alert>
        ) : null}
        <TextField
          sx={{ mb: withSnapshot ? 1 : 2 }}
          label="Filter name"
          variant="outlined"
          size="small"
          fullWidth
          value={name}
          onChange={e => setName(e.target.value)}
        />
        {withSnapshot && (
          <FormControlLabel
            sx={{ mb: 1 }}
            control={
              <Switch
                checked={snapshotMode}
                onChange={e => setSnapshotMode(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2">
                Build snapshot — select which matched components to freeze as membership
              </Typography>
            }
          />
        )}
        <FilterBuilder
          value={draft}
          onChange={setDraft}
          snapshotMode={snapshotMode}
          onSnapshotSelectionChange={refs => {
            snapshotRefsRef.current = refs;
            setSnapshotCount(refs.length);
          }}
        />
      </DialogContent>
      <DialogActions>
        {filter ? (
          <Button
            sx={{ mr: 'auto', color: 'error.main' }}
            disabled={saving || deleteFilter.isPending}
            onClick={() => setConfirmingDelete(true)}
          >
            Delete filter
          </Button>
        ) : null}
        <Button onClick={onClose}>Cancel</Button>
        <Button
          color="primary"
          variant="contained"
          disabled={!name.trim() || saving || (snapshotMode && snapshotCount === 0)}
          onClick={() => {
            // Surface errors via actionError instead of an unhandled rejection.
            handleSave().catch(() => undefined);
          }}
        >
          {saving
            ? 'Saving…'
            : snapshotMode
              ? `Save & freeze ${snapshotCount} component${snapshotCount === 1 ? '' : 's'}`
              : 'Save filter'}
        </Button>
      </DialogActions>

      <Dialog open={confirmingDelete} onClose={() => setConfirmingDelete(false)}>
        <DialogTitle>Delete this filter?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            "{filter?.name}" will be permanently deleted. Any campaign still
            using it will fall back to targeting all components.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmingDelete(false)}>Cancel</Button>
          <Button
            color="secondary"
            variant="contained"
            disabled={deleteFilter.isPending}
            onClick={() => {
              handleDelete().catch(() => undefined);
            }}
          >
            {deleteFilter.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};
